import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { Building2, Trophy, Users, Calendar, Loader2, Crown, Sparkles, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { id: 'weekly', label: 'This week' },
  { id: 'monthly', label: 'This month' },
  { id: 'all', label: 'All time' },
];

const RANK_BADGE = (rank) => {
  if (rank === 1) return 'bg-amber-400 text-amber-900';
  if (rank === 2) return 'bg-slate-300 text-slate-700';
  if (rank === 3) return 'bg-orange-400 text-orange-900';
  return 'bg-slate-100 text-slate-500';
};

const Institutes = () => {
  const { user, refreshUser } = useAuth();
  const [period, setPeriod] = useState('weekly');
  const [board, setBoard] = useState(null);
  const [contests, setContests] = useState([]);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(user?.institute || '');
  const [saving, setSaving] = useState(false);
  const [registering, setRegistering] = useState('');

  const fetchBoard = async (p) => {
    try { const { data } = await axios.get(`${API_URL}/api/institutes/leaderboard?period=${p}`); setBoard(data); }
    catch (e) { console.error('Failed to load institute leaderboard:', e); }
  };
  const fetchContests = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/institute-contests`); setContests(data.contests || []); }
    catch (e) { console.error('Failed to load institute contests:', e); }
  };
  const fetchList = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/institutes/list`); setList(data.institutes || []); }
    catch (e) { console.error('Failed to load institute list:', e); }
  };

  useEffect(() => { fetchList(); fetchContests(); }, []);
  useEffect(() => { fetchBoard(period); }, [period]);

  const saveInstitute = async () => {
    if (!selected) { toast.error('Pick an institute'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/institutes/me`, { institute: selected });
      toast.success('Institute set! You can now compete on the leaderboard.');
      await refreshUser();
      await fetchBoard(period);
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const register = async (id) => {
    setRegistering(id);
    try {
      await axios.post(`${API_URL}/api/institute-contests/${id}/register`);
      toast.success('Registered! See you in the contest 🚀');
      await fetchContests();
    } catch (e) { toast.error('Failed to register'); }
    finally { setRegistering(''); }
  };

  return (
    <div className="min-h-screen" data-testid="institutes-page">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Building2 className="w-4 h-4" /> Institutes Collab
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#111827] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="institutes-title">
            Institute vs Institute. Bragging rights live here.
          </h1>
          <p className="text-base text-[#6B7280] max-w-2xl mx-auto">
            Cross-institute leaderboards across India. Compete with students from Allen, PW, Aakash, FIITJEE, and more.
          </p>
        </div>

        {/* My institute selector */}
        <div className="rounded-3xl p-5 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 mb-8" data-testid="my-institute-card">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">Your institute</p>
              <p className="text-base font-bold text-[#111827]">
                {user?.institute || 'Not set yet — pick yours to join the leaderboard'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <select value={selected} onChange={(e) => setSelected(e.target.value)} className="px-4 py-2.5 border-2 border-emerald-300 rounded-xl bg-white outline-none focus:border-emerald-500 text-sm" data-testid="institute-select">
                <option value="">Select institute</option>
                {list.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
              <button onClick={saveInstitute} disabled={saving} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-sm transition disabled:opacity-50 flex items-center gap-2" data-testid="institute-save-button">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Check className="w-4 h-4" /> Set institute</>}
              </button>
            </div>
          </div>
        </div>

        {/* Cross-institute leaderboard */}
        <div className="rounded-3xl bg-white border border-slate-100 shadow-sm overflow-hidden mb-8" data-testid="institute-leaderboard">
          <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-[#111827] flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                <Trophy className="w-5 h-5 text-amber-500" /> Institute leaderboard
              </h2>
              <p className="text-xs text-[#6B7280]">Avg score across all live quiz attempts.</p>
            </div>
            <div className="flex gap-1.5 bg-slate-100 rounded-full p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${period === p.id ? 'bg-white text-[#0EA5E9] shadow' : 'text-[#6B7280] hover:text-[#111827]'}`}
                  data-testid={`institute-period-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            {!board ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5E9]" /></div>
            ) : board.institutes?.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#9CA3AF]" data-testid="institute-leaderboard-empty">
                No quiz attempts yet for this period. Be the first to put your institute on the map!
              </div>
            ) : (
              <ul>
                {board.institutes.map((row) => (
                  <li key={row.institute} className="flex items-center gap-4 px-5 py-4 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition" data-testid={`institute-row-${row.rank}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-sm ${RANK_BADGE(row.rank)}`} style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      {row.rank === 1 ? <Crown className="w-4 h-4" /> : `#${row.rank}`}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-[#111827]">{row.institute}</p>
                      <p className="text-xs text-[#9CA3AF]">{row.active_users} students · {row.total_attempts} attempts</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-black text-[#0EA5E9]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{row.avg_score}</p>
                      <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Avg score</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Institute-hosted contests */}
        <div data-testid="institute-contests">
          <h2 className="text-xl font-bold text-[#111827] mb-3 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <Sparkles className="w-5 h-5 text-amber-500" /> Live institute scholarship contests
          </h2>
          {contests.length === 0 ? (
            <div className="rounded-2xl p-6 bg-white border border-slate-100 shadow-sm text-center text-sm text-[#9CA3AF]" data-testid="institute-contests-empty">
              No live scholarship contests right now. Check back soon — we partner with new institutes every month.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {contests.map((c, idx) => (
                <div key={c.id} className="p-5 rounded-2xl bg-white border border-slate-100 shadow-sm" data-testid={`institute-contest-${idx}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700">
                        {c.institute_name}
                      </span>
                      <h3 className="mt-1.5 font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{c.title}</h3>
                      <p className="text-xs text-[#6B7280] mt-0.5">{c.subject} · {c.topic}</p>
                    </div>
                    <span className="px-2 py-1 rounded-lg bg-amber-100 text-amber-700 text-xs font-bold">FREE</span>
                  </div>
                  {c.prize_description && <p className="text-sm text-[#374151] mb-3"><Trophy className="w-4 h-4 inline text-amber-500 mr-1" />{c.prize_description}</p>}
                  <div className="flex items-center justify-between text-xs text-[#9CA3AF] mb-3">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(c.starts_at).toLocaleString()}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.participants_count || 0} registered</span>
                  </div>
                  <button
                    onClick={() => register(c.id)}
                    disabled={registering === c.id}
                    className="w-full px-4 py-2.5 bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-xl font-bold text-sm transition disabled:opacity-50"
                    data-testid={`institute-contest-register-${idx}`}
                  >
                    {registering === c.id ? 'Registering…' : 'Register for free'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Institutes;
